import React, { useState, useEffect, useMemo } from "react";
import { roleOptionsForCompany } from "../../../utils/roles";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

import api from "../../../services/api";
import { endpoints } from "../../../services/endpoints";

export default function CreateUser() {
  const navigate = useNavigate();

  const [user, setUser] = useState({
    name: "",
    email: "",
    identification: "",
    phone: "",
    typeUser: null,     // ADMIN | SUPERVISOR | OPERATOR
    active: true,
    companyId: null,
  });

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(endpoints.companies.list);
        const list = res.data?.data ?? res.data ?? [];
        const formatted = (Array.isArray(list) ? list : []).map((c) => ({
          value: c.id,
          label: c.name,
        }));
        setCompanies(formatted);
      } catch (err) {
        console.error(err);
        alert("Error fetching companies");
      }
    })();
  }, []);

  const allowedRoleOptions = useMemo(
    () => roleOptionsForCompany(selectedCompany?.label),
    [selectedCompany?.label]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (e) => {
    const { name, checked } = e.target;
    setUser((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    if (name === "companyId") {
      setSelectedCompany(selectedOption || null);
      const newCompanyId = selectedOption ? selectedOption.value : null;
      const newAllowed = roleOptionsForCompany(selectedOption?.label).map((r) => r.value);

      setUser((prev) => ({
        ...prev,
        companyId: newCompanyId,
        typeUser: prev.typeUser && newAllowed.includes(prev.typeUser) ? prev.typeUser : null,
      }));
      return;
    }

    // name === "typeUser"
    setUser((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user.typeUser) {
      alert("Selecciona un rol (typeUser).");
      return;
    }
    if (!user.companyId) {
      alert("Selecciona una compañía.");
      return;
    }

    try {
      setSaving(true);

      // 4) Armar payload (enviamos el hash en el campo password)
      const payload = {
        name: user.name.trim(),
        email: user.email.trim(),
        identification: user.identification.trim(),
        phone: (user.phone ?? '').trim(),
        typeuser: user.typeUser,
        companyId: Number(user.companyId),
        active: !!user.active,
      };

      await api.post(endpoints.users.create, payload, {
        headers: { "Content-Type": "application/json" },
      });

      navigate("/dashboardusersadmin");
    } catch (err) {
      console.error(err);
      const r = err?.response;
      const msg =
        Array.isArray(r?.data?.message) ? r.data.message.join("\n") :
        r?.data?.message || r?.data?.error || "Error creating user";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-center mb-6">Create User</h1>

      <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow space-y-4 max-w-lg mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <label className="block font-medium">Name</label>
            <input
              type="text"
              name="name"
              value={user.name}
              onChange={handleChange}
              className="border rounded w-full p-2"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block font-medium">Email</label>
            <input
              type="email"
              name="email"
              value={user.email}
              onChange={handleChange}
              className="border rounded w-full p-2"
              required
            />
          </div>

          {/* Identification */}
          <div>
            <label className="block font-medium">Identification</label>
            <input
              type="text"
              name="identification"
              value={user.identification}
              onChange={handleChange}
              className="border rounded w-full p-2"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block font-medium">Phone</label>
            <input
              type="text"
              name="phone"
              value={user.phone}
              onChange={handleChange}
              className="border rounded w-full p-2"
            />
          </div>

          {/* Company */}
          <div>
            <label className="block font-medium">Company</label>
            <Select
              options={companies}
              name="companyId"
              onChange={handleSelectChange}
              isClearable
              placeholder="Select company"
              value={companies.find((c) => c.value === user.companyId) || null}
            />
            <p className="text-xs text-gray-500 mt-1">
              Si la compañía es <b>cloudtainer</b>, solo se permite el rol <b>ADMIN</b>.
              En el resto, solo <b>SUPERVISOR</b> y <b>OPERATOR</b>.
            </p>
          </div>

          {/* Role (typeUser) */}
          <div>
            <label className="block font-medium">Role</label>
            <Select
              options={allowedRoleOptions}
              name="typeUser"
              onChange={handleSelectChange}
              isClearable
              isDisabled={!selectedCompany}
              placeholder={selectedCompany ? "Select role" : "Select company first"}
              value={allowedRoleOptions.find((o) => o.value === user.typeUser) || null}
            />
          </div>

          {/* Active */}
          <div className="flex items-center gap-2">
            <input
              id="active"
              type="checkbox"
              name="active"
              checked={user.active}
              onChange={handleCheckbox}
              className="h-4 w-4"
            />
            <label htmlFor="active" className="font-medium">
              Active
            </label>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-60"
          >
            {saving ? "Creating…" : "Create User"}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="bg-gray-500 text-white px-4 py-2 rounded"
          >
            Cerrar
          </button>
        </div>
      </form>
    </div>
  );
}